import React, { useState, useMemo } from "react";
import { Card, Row, Col, Spinner, Alert, Form, InputGroup, Stack, Offcanvas } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faInfoCircle,
    faPills,
    faSearch,
    faTimes,
    faPlay,
    faCheck,
    faStop,
    faFileImport,
    faCalendarAlt,
    faEdit,
    faShoppingCart,
    faExclamationTriangle,
    faBriefcase,
    faEllipsisV,
    faBars,
    faPlus,
    faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { inventarioBreadcrumbItems, ProdutoParaInventarioType } from "data/sngpc";
import { useInventarioAtivo } from "hooks/useSngpc";
import { useEmpresas } from "providers/EmpresaProvider";
import { useBreakpoints } from "providers/BreakpointsProvider";
import Button from "components/base/Button";
import { format } from 'date-fns/format';

// Componentes modulares
import BotaoCriarInventario from "./components/BotaoCriarInventario";
import BotaoAlterarData from "./components/BotaoAlterarData";
import BotaoValidarInventario from "./components/BotaoValidarInventario";
import BotaoFinalizarInventario from "./components/BotaoFinalizarInventario";
import BotaoExportarXml from "./components/BotaoExportarXml";
import BotaoPesquisarProdutos from "./components/BotaoPesquisarProdutos";
import BotaoImportarEstoque from "./components/BotaoImportarEstoque";
import LinkProduto from "../../../components/common/LinkProduto";
import ModalIncluirProduto from "./components/ModalIncluirProduto";
import ModalEditarProduto from "./components/ModalEditarProduto";
import ModalExcluirProduto from "./components/ModalExcluirProduto";

const InventarioSngpc: React.FC = () => {
    // Estados para modais de produto
    const [showModalIncluirProduto, setShowModalIncluirProduto] = useState(false);
    const [produtoParaIncluir, setProdutoParaIncluir] = useState<ProdutoParaInventarioType | null>(null);

    const [showModalEditarProduto, setShowModalEditarProduto] = useState(false);
    const [produtoParaEditar, setProdutoParaEditar] = useState<any>(null);

    const [showModalExcluirProduto, setShowModalExcluirProduto] = useState(false);
    const [produtoParaExcluir, setProdutoParaExcluir] = useState<any>(null);

    // Estado para busca na lista de produtos
    const [termoBusca, setTermoBusca] = useState('');

    // Estado para menu móvel de ações
    const [showMobileActions, setShowMobileActions] = useState(false);

    // Hooks
    const { selectedIds, empresas } = useEmpresas();
    const inventarioQuery = useInventarioAtivo();
    const { breakpoints } = useBreakpoints();

    const inventario = inventarioQuery.data?.data;

    // Verificações responsivas
    const isMobile = breakpoints.down('md');
    const isTablet = breakpoints.up('md') && breakpoints.down('lg');
    const isDesktop = breakpoints.up('lg');

    // Verificar se há múltiplas empresas selecionadas
    const hasMultipleEmpresas = selectedIds.length > 1;
    const empresaSelecionada = empresas.find(e => e.id === selectedIds[0]);

    // Verifica se há erros de permissão
    const hasPermissionError = inventarioQuery.isError;

    // Handlers para modais
    const handleSelecionarProduto = (produto: ProdutoParaInventarioType) => {
        setProdutoParaIncluir(produto);
        setShowModalIncluirProduto(true);
    };

    const handleEditarProduto = (produto: any) => {
        setProdutoParaEditar(produto);
        setShowModalEditarProduto(true);
    };

    const handleExcluirProduto = (produto: any) => {
        setProdutoParaExcluir(produto);
        setShowModalExcluirProduto(true);
    };

    const handleAdicionarLoteExistente = (produto: any) => {
        const produtoParaIncluir: ProdutoParaInventarioType = {
            id: produto.produtoId,
            nome: produto.nomeProduto || produto.produtoDescricao || '',
            codigo: produto.produtoId,
            codigoBarras: produto.produtoCodigoBarras || undefined,
            fabricante: produto.fabricante || undefined,
            ehControlado: true,
            estoqueAtual: produto.quantidade || 0,
            unidade: undefined,
            jaNoInventario: true,
            rms: produto.rms || undefined,
            lista: produto.lista,
            classeTerapeutica: produto.classeTerapeutica,
            receituario: produto.receituario
        };

        setProdutoParaIncluir(produtoParaIncluir);
        setShowModalIncluirProduto(true);
    };

    // Filtrar produtos baseado no termo de busca
    const produtosFiltrados = useMemo(() => {
        if (!inventario?.produtos || !termoBusca.trim()) {
            return inventario?.produtos || [];
        }

        const termo = termoBusca.toLowerCase();
        const termoNormalizado = termo.replace(/[^\w]/g, '');

        return inventario.produtos.filter(produto =>
            produto.nomeProduto?.toLowerCase().includes(termo) ||
            produto.produtoId?.toString().includes(termo) ||
            produto.fabricante?.toLowerCase().includes(termo) ||
            produto.numero?.toLowerCase().includes(termo) ||
            produto.lote?.toLowerCase().includes(termo) ||
            produto.rms?.toLowerCase().includes(termo) ||
            produto.rms?.replace(/[^\w]/g, '').toLowerCase().includes(termoNormalizado)
        );
    }, [inventario?.produtos, termoBusca]);

    // Render do indicador de status simplificado para mobile
    const renderStatusIndicator = () => {
        if (!inventario) return null;

        const statusItems = [
            { icon: faPlay, label: "Criado", active: true, color: "success" },
            { icon: faCheck, label: "Validado", active: inventario.validado, color: "warning" },
            { icon: faStop, label: "Finalizado", active: inventario.finalizado, color: "danger" }
        ];

        if (isMobile) {
            // Versão compacta para mobile
            const activeStatus = statusItems.find(item => item.active) || statusItems[0];
            return (
                <div className="d-flex align-items-center">
                    <div className={`badge bg-${activeStatus.color} d-flex align-items-center`}>
                        <FontAwesomeIcon icon={activeStatus.icon} className="me-1" size="sm" />
                        <small>{activeStatus.label}</small>
                    </div>
                </div>
            );
        }

        // Versão completa para desktop/tablet
        return (
            <div className="d-flex align-items-center gap-2">
                {statusItems.map((item, index) => (
                    <React.Fragment key={item.label}>
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon
                                icon={item.icon}
                                className={item.active ? `text-${item.color}` : "text-body-secondary"}
                                size="sm"
                            />
                            <small className={`ms-1 ${item.active ? `text-${item.color}` : "text-body-secondary"}`}>
                                {item.label}
                            </small>
                        </div>
                        {index < statusItems.length - 1 && (
                            <FontAwesomeIcon icon={faPlay} className="text-body-secondary" size="xs" style={{ transform: 'rotate(180deg)' }} />
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // Render de botões de ação principal
    const renderActionButtons = () => {
        const buttons = [];

        if (inventario && !inventario.validado && !inventario.finalizado) {
            buttons.push(
                <BotaoExportarXml key="export" disabled={hasMultipleEmpresas || selectedIds.length === 0} />,
                <BotaoValidarInventario key="validate" disabled={hasMultipleEmpresas || selectedIds.length === 0} />
            );
        }

        if (inventario && inventario.validado && !inventario.finalizado) {
            buttons.push(
                <BotaoFinalizarInventario key="finalize" disabled={hasMultipleEmpresas || selectedIds.length === 0} />
            );
        }

        if (isMobile && buttons.length > 0) {
            return (
                <>
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowMobileActions(true)}
                        className="d-flex align-items-center"
                    >
                        <FontAwesomeIcon icon={faEllipsisV} />
                    </Button>

                    <Offcanvas
                        show={showMobileActions}
                        onHide={() => setShowMobileActions(false)}
                        placement="bottom"
                    >
                        <Offcanvas.Header closeButton>
                            <Offcanvas.Title>Ações do Inventário</Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Stack gap={2}>
                                {buttons}
                            </Stack>
                        </Offcanvas.Body>
                    </Offcanvas>
                </>
            );
        }

        return (
            <div className="d-flex gap-2 flex-wrap">
                {buttons}
            </div>
        );
    };

    // Render de produtos em formato card para mobile
    const renderProdutoCard = (item: any, index: number) => (
        <Card key={`${item.produtoId}-${item.numero || item.lote || index}`} className="mb-3 shadow-sm">
            <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1 me-2">
                        <LinkProduto produtoId={item.produtoId}>
                            <h6 className="mb-1 text-primary-emphasis">{item.nomeProduto || item.produtoDescricao || 'Produto sem descrição'}</h6>
                        </LinkProduto>
                        <div className="small text-body-secondary mb-1">
                            Código: {item.produtoCodigo || item.produtoId}
                            {item.produtoCodigoBarras && ` • EAN: ${item.produtoCodigoBarras}`}
                        </div>
                        {item.fabricante && item.fabricante.trim() && (
                            <div className="small text-body-secondary">
                                Fabricante: {item.fabricante}
                            </div>
                        )}
                    </div>
                    <div className="text-end">
                        <div className="fw-bold mb-1">{item.quantidade || 0}</div>
                        <div className="small text-body-secondary">unidades</div>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-2 flex-wrap">
                        {(item.numero && item.numero.trim()) || (item.lote && item.lote.trim()) ? (
                            <span className="badge bg-secondary small">
                                Lote: {item.numero || item.lote}
                            </span>
                        ) : (
                            <span className="badge bg-body-secondary text-body-emphasis small">Sem lote</span>
                        )}
                        {item.rms && (
                            <span className="badge bg-info text-dark small">
                                RMS: {item.rms}
                            </span>
                        )}
                    </div>

                    <div className="d-flex gap-1">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditarProduto(item)}
                            disabled={inventario?.validado || inventario?.finalizado || hasMultipleEmpresas || selectedIds.length === 0}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleExcluirProduto(item)}
                            disabled={inventario?.validado || inventario?.finalizado || hasMultipleEmpresas || selectedIds.length === 0}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </Button>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );

    // Render da tabela para desktop
    const renderProdutoTable = () => (
        <div className="table-responsive">
            <table className="table table-hover">
                <thead>
                    <tr>
                        <th>Produto</th>
                        <th>Lote</th>
                        <th>Quantidade</th>
                        <th>RMS</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {produtosFiltrados.map((item, index) => (
                        <tr key={`${item.produtoId}-${item.numero || item.lote || index}`}>
                            <td>
                                <div>
                                    <LinkProduto produtoId={item.produtoId}>
                                        <strong>{item.nomeProduto || item.produtoDescricao || 'Produto sem descrição'}</strong>
                                    </LinkProduto>
                                    <div className="small text-body-secondary">
                                        Código: {item.produtoCodigo || item.produtoId}
                                        {item.produtoCodigoBarras && ` | EAN: ${item.produtoCodigoBarras}`}
                                    </div>
                                    {item.fabricante && item.fabricante.trim() && (
                                        <div className="small text-body-secondary">
                                            Fabricante: {item.fabricante}
                                        </div>
                                    )}
                                </div>
                            </td>
                            <td>
                                {(item.numero && item.numero.trim()) || (item.lote && item.lote.trim()) ? (
                                    <span className="badge bg-secondary">
                                        {item.numero || item.lote}
                                    </span>
                                ) : (
                                    <span className="text-body-secondary small">Sem lote</span>
                                )}
                            </td>
                            <td>
                                <span className="fw-bold">{item.quantidade || 0}</span>
                            </td>
                            <td>
                                <code className="small">{item.rms || '-'}</code>
                            </td>
                            <td>
                                <div className="d-flex gap-1">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleEditarProduto(item)}
                                        disabled={inventario?.validado || inventario?.finalizado || hasMultipleEmpresas || selectedIds.length === 0}
                                        title="Editar produto"
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleExcluirProduto(item)}
                                        disabled={inventario?.validado || inventario?.finalizado || hasMultipleEmpresas || selectedIds.length === 0}
                                        title="Excluir produto"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    if (inventarioQuery.isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </div>
        );
    }

    if (hasPermissionError) {
        return (
            <div className="text-center py-5 text-danger-emphasis">
                {(inventarioQuery.error as Error).message}
            </div>
        );
    }

    return (
        <div>
            <PageBreadcrumb items={inventarioBreadcrumbItems} />

            {/* Header Responsivo */}
            <div className={`d-flex ${isMobile ? 'flex-column gap-3' : 'justify-content-between align-items-center'} mb-4`}>
                <div className={isMobile ? 'text-center' : ''}>
                    <h2 className="mb-2">
                        Inventário SNGPC
                    </h2>
                    <p className="text-body-tertiary mb-0">
                        Gerencie o inventário de produtos controlados
                    </p>
                </div>

                {/* Info da empresa e status */}
                {empresaSelecionada && (
                    <div className={`d-flex ${isMobile ? 'flex-column gap-2 align-items-center' : 'align-items-center gap-3'}`}>
                        <div className="d-flex align-items-center text-body-secondary">
                            <FontAwesomeIcon icon={faBriefcase} className="me-2" />
                            <small>
                                <strong>{empresaSelecionada.nomeFantasia}</strong>
                            </small>
                        </div>
                        {inventario && renderStatusIndicator()}
                    </div>
                )}
            </div>

            {/* Alertas */}
            {hasMultipleEmpresas && (
                <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-4">
                    <div className="text-warning-emphasis">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <strong>Múltiplas empresas selecionadas:</strong> Para trabalhar com inventários SNGPC,
                        selecione apenas uma empresa no seletor de empresas. Atualmente está sendo usada: <strong>{empresaSelecionada?.nomeFantasia}</strong>
                    </div>
                </Alert>
            )}

            {selectedIds.length === 0 && (
                <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-4">
                    <div className="text-warning-emphasis">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <strong>Nenhuma empresa selecionada:</strong> Selecione uma empresa no seletor de empresas para trabalhar com inventários SNGPC.
                    </div>
                </Alert>
            )}

            {!inventario && selectedIds.length > 0 ? (
                // Estado: Nenhum inventário ativo - Layout responsivo
                <div>
                    <Card className="text-center py-4 mb-4">
                        <Card.Body>
                            <FontAwesomeIcon icon={faBoxOpen} size={isMobile ? "3x" : "4x"} className="text-primary-emphasis mb-3" />
                            <h3 className={`mb-3 ${isMobile ? 'h4' : ''}`}>Iniciar Novo Inventário</h3>
                            <p className="text-body-tertiary mb-4">
                                Não há inventário em andamento. Escolha uma das opções abaixo para iniciar.
                            </p>
                        </Card.Body>
                    </Card>

                    <Row className="g-3">
                        <Col md={6} className="mb-3">
                            <Card className="h-100 border-primary border-opacity-25 shadow-sm">
                                <Card.Body className={`text-center ${isMobile ? 'p-3' : 'p-4'}`}>
                                    <FontAwesomeIcon icon={faFileImport} size={isMobile ? "2x" : "3x"} className="text-primary-emphasis mb-3" />
                                    <h5 className={isMobile ? 'h6' : 'h5'}>Importar do Estoque</h5>
                                    <p className="text-body-tertiary mb-3 small">
                                        Importa automaticamente todos os produtos controlados que possuem estoque atual.
                                    </p>
                                    <BotaoCriarInventario
                                        tipo="importar"
                                        disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6} className="mb-3">
                            <Card className="h-100 border-success border-opacity-25 shadow-sm">
                                <Card.Body className={`text-center ${isMobile ? 'p-3' : 'p-4'}`}>
                                    <FontAwesomeIcon icon={faPlay} size={isMobile ? "2x" : "3x"} className="text-success-emphasis mb-3" />
                                    <h5 className={isMobile ? 'h6' : 'h5'}>Inventário Manual</h5>
                                    <p className="text-body-tertiary mb-3 small">
                                        Cria um inventário vazio para adicionar produtos manualmente conforme necessário.
                                    </p>
                                    <BotaoCriarInventario
                                        tipo="manual"
                                        disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mt-4">
                        <div className="text-info-emphasis">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            <strong>Dica:</strong> Após criar o inventário, você poderá adicionar produtos manualmente,
                            importar do estoque ou fazer ajustes antes da validação e transmissão.
                        </div>
                    </Alert>
                </div>
            ) : (
                <>
                    {/* Informações do Inventário - Layout responsivo */}
                    <Card className="mb-4">
                        <Card.Body>
                            <div className={`d-flex ${isMobile ? 'flex-column gap-3' : 'justify-content-between align-items-center'}`}>
                                <div>
                                    <h5 className="mb-2">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary-emphasis" />
                                        Data do Inventário
                                    </h5>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <strong>
                                            {inventario?.data && !isNaN(new Date(inventario.data).getTime())
                                                ? format(new Date(inventario.data), 'dd/MM/yyyy')
                                                : 'N/A'
                                            }
                                        </strong>
                                        {inventario && !inventario.validado && !inventario.finalizado && (
                                            <BotaoAlterarData
                                                inventario={inventario}
                                                disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className={isMobile ? 'd-flex justify-content-center' : ''}>
                                    {renderActionButtons()}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Alertas de Status */}
                    {inventario && inventario.validado && !inventario.finalizado && (
                        <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-4">
                            <div className="text-warning-emphasis">
                                <FontAwesomeIcon icon={faCheck} className="me-2" />
                                <strong>Inventário Validado</strong> - O inventário foi validado e está pronto para ser finalizado.
                            </div>
                        </Alert>
                    )}

                    {inventario && inventario.finalizado && (
                        <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mb-4">
                            <div className="text-info-emphasis">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                <strong>Inventário Finalizado</strong> - Este inventário foi finalizado e não pode mais ser modificado.
                            </div>
                        </Alert>
                    )}

                    {/* Produtos do Inventário */}
                    <Card>
                        <Card.Header className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'}`}>
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faBars} className="me-2" />
                                Produtos ({inventario?.produtos?.length || 0})
                            </h5>
                            {inventario && !inventario.validado && !inventario.finalizado && (
                                <div className={`d-flex gap-2 ${isMobile ? 'w-100' : ''}`}>
                                    <BotaoImportarEstoque disabled={hasMultipleEmpresas || selectedIds.length === 0} />
                                    <BotaoPesquisarProdutos
                                        onProdutoSelecionado={handleSelecionarProduto}
                                        disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                    />
                                </div>
                            )}
                        </Card.Header>
                        <Card.Body className={isMobile ? 'p-2' : ''}>
                            {(!inventario?.produtos || inventario.produtos.length === 0) ? (
                                <div className="text-center py-4">
                                    <FontAwesomeIcon icon={faShoppingCart} size="3x" className="text-body-secondary mb-3" />
                                    <h5 className="text-body-secondary mb-3">Nenhum produto no inventário</h5>
                                    <p className="text-body-secondary mb-4">
                                        {inventario && (inventario.validado || inventario.finalizado)
                                            ? "Este inventário não possui produtos ou foi finalizado."
                                            : "Adicione produtos ao inventário usando os botões acima."
                                        }
                                    </p>
                                    {inventario && !inventario.validado && !inventario.finalizado && (
                                        <div className={`d-flex gap-2 ${isMobile ? 'flex-column align-items-center' : 'justify-content-center'}`}>
                                            <BotaoImportarEstoque disabled={hasMultipleEmpresas || selectedIds.length === 0} />
                                            <BotaoPesquisarProdutos
                                                onProdutoSelecionado={handleSelecionarProduto}
                                                disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    {/* Busca responsiva */}
                                    <div className="mb-3">
                                        <Row className="align-items-center g-2">
                                            <Col>
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon={faSearch} />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder={isMobile ? "Buscar produto..." : "Buscar por produto, código, fabricante, lote ou RMS..."}
                                                        value={termoBusca}
                                                        onChange={(e) => setTermoBusca(e.target.value)}
                                                    />
                                                    {termoBusca && (
                                                        <Button
                                                            variant="outline-secondary"
                                                            onClick={() => setTermoBusca('')}
                                                            title="Limpar busca"
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </Button>
                                                    )}
                                                </InputGroup>
                                            </Col>
                                            {!isMobile && (
                                                <Col xs="auto">
                                                    <small className="text-body-secondary">
                                                        {termoBusca ? (
                                                            `${produtosFiltrados.length} de ${inventario?.produtos?.length || 0} produtos`
                                                        ) : (
                                                            `${inventario?.produtos?.length || 0} produtos`
                                                        )}
                                                    </small>
                                                </Col>
                                            )}
                                        </Row>
                                        {isMobile && (
                                            <div className="text-center mt-2">
                                                <small className="text-body-secondary">
                                                    {termoBusca ? (
                                                        `${produtosFiltrados.length} de ${inventario?.produtos?.length || 0} produtos encontrados`
                                                    ) : (
                                                        `${inventario?.produtos?.length || 0} produtos no inventário`
                                                    )}
                                                </small>
                                            </div>
                                        )}
                                    </div>

                                    {produtosFiltrados.length === 0 && termoBusca ? (
                                        <div className="text-center py-4">
                                            <FontAwesomeIcon icon={faSearch} size="3x" className="text-body-secondary mb-3" />
                                            <h5 className="text-body-secondary mb-3">Nenhum produto encontrado</h5>
                                            <p className="text-body-secondary mb-4">
                                                Não foram encontrados produtos que correspondam à busca "{termoBusca}".
                                            </p>
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => setTermoBusca('')}
                                            >
                                                <FontAwesomeIcon icon={faTimes} className="me-2" />
                                                Limpar Busca
                                            </Button>
                                        </div>
                                    ) : (
                                        <div>
                                            {isMobile ? (
                                                // Layout em cards para mobile
                                                <div className={isMobile ? 'px-2' : ''}>
                                                    {produtosFiltrados.map((item, index) => renderProdutoCard(item, index))}
                                                </div>
                                            ) : (
                                                // Layout em tabela para desktop
                                                renderProdutoTable()
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </>
            )}

            {/* Modais */}
            <ModalIncluirProduto
                show={showModalIncluirProduto}
                onHide={() => {
                    setShowModalIncluirProduto(false);
                    setProdutoParaIncluir(null);
                }}
                produto={produtoParaIncluir}
            />

            <ModalEditarProduto
                show={showModalEditarProduto}
                onHide={() => {
                    setShowModalEditarProduto(false);
                    setProdutoParaEditar(null);
                }}
                produto={produtoParaEditar}
                inventarioId={inventario?.id}
                onAdicionarLote={handleAdicionarLoteExistente}
            />

            <ModalExcluirProduto
                show={showModalExcluirProduto}
                onHide={() => {
                    setShowModalExcluirProduto(false);
                    setProdutoParaExcluir(null);
                }}
                produto={produtoParaExcluir}
                inventarioId={inventario?.id}
            />
        </div>
    );
};

export default InventarioSngpc; 